import * as R from 'ramda';
import num from 'numeral';
import { User } from 'utils/apiClient/services/contacts/types';
import paymentMethods from 'utils/paymentMethods';
import chroma from 'chroma-js';
import dayjs from 'dayjs';
import theme from 'assets/theme';
import { JobState } from 'Common/types';
import { assertNever } from 'Common/utils';

import type {
  InvoiceReport,
  InvoiceReportRecord,
  ProductReport,
  ProductReportRecord,
  ProductSoldReportRecord,
  IncomeTransactionsReportRecord,
  BasicReportRecord,
  RevenueAndProfitChartData,
  ReportType,
  SalesReport,
  BatchReportRecord,
  BatchReportRawDetails,
  LastTransactionsTableInputData,
  LastTransactionsTableEntry,
} from 'utils/apiClient/services/reports/types';

import {
  BatchReportType,
  BatchReportStatus,
} from 'utils/apiClient/services/reports/types';
import { BestsellerOverview } from './components/views/RevenueOverviewBestsellersTable';

const localLocale = dayjs();
localLocale.locale('en');

const formatUserName = (user?: User) =>
  (user && [user.first_name, user.last_name].filter((v) => v).join(' ')) || '';

const defaultToZero = R.defaultTo(0);
export const numOrZero = (v: string | number | null | undefined) => {
  // Work-around for the bug reported here:
  // https://github.com/adamwdraper/Numeral-js/pull/601
  // seems that numeral isn't being maintained anymore
  if (typeof v === 'string') {
    return defaultToZero(num(R.replace('E', 'e', v)).value());
  }
  return defaultToZero(num(v).value());
};

const reduceProductReportsToUser = ({
  reports,
  user,
}: {
  reports: Array<ProductReport>;
  user: User;
}): ProductReportRecord =>
  reports.reduce(
    (acc, r) =>
      r.user.id === user.id
        ? {
            ...acc,
            amount_sold: r.amount_sold + acc.amount_sold,
            revenue: parseFloat(r.revenue) + acc.revenue,
            returned_amount:
              parseFloat(r.returned_amount) + acc.returned_amount,
            returned: r.returned + acc.returned,
            discount: {
              amount: parseFloat(r.discount.amount) + acc.discount.amount,
              percent: parseFloat(r.discount.percent) + acc.discount.percent,
            },
            baskets: r.baskets + acc.baskets,
          }
        : acc,
    {
      id: user.id,
      name: formatUserName(user),
      amount_sold: 0,
      revenue: 0,
      returned_amount: 0,
      returned: 0,
      discount: {
        amount: 0,
        percent: 0,
      },
      baskets: 0,
    }
  );

export const mapProductReportsToUser = ({
  reports,
  users,
}: {
  reports: Array<ProductReport>;
  users: Array<User>;
}): Array<ProductReportRecord> =>
  users.map((user) => reduceProductReportsToUser({ user, reports }));

export const filterProductReportsForUser = ({
  reports,
  user_id,
}: {
  reports: Array<ProductReport>;
  user_id: number;
}): Array<ProductReportRecord> =>
  reports
    .filter((r) => r.user.id === user_id)
    .map((r) => ({
      amount_sold: r.amount_sold,
      returned: r.returned,
      baskets: r.baskets,
      revenue: parseFloat(r.revenue),
      returned_amount: parseFloat(r.returned_amount),
      name: r.product.name,
      id: r.product.id,
      discount: {
        amount: parseFloat(r.discount.amount),
        percent: parseFloat(r.discount.percent),
      },
    }));

export const normalizeInvoiceReports = ({
  reports,
  users,
}: {
  reports: Array<InvoiceReport>;
  users: Array<User>;
}): Array<InvoiceReportRecord> =>
  reports.map((r) => ({
    id: r.user.id,
    name: formatUserName(users.find((u) => u.id === r.user.id)),
    walk_in_ratio: r.walk_in_ratio,
    checkouts: r.checkouts,
    average_value: parseFloat(r.average_value),
    total_value: parseFloat(r.total_value),
  }));

// ==========================================================================
//                    Generate payment by method report
// ==========================================================================
export interface RevenueByPaymentReportRecord {
  method: string;
  value: number;
}

const sortDescendingByValue = R.sortWith<RevenueByPaymentReportRecord>([
  R.descend(R.prop('value')),
]);

const mapPayments = R.compose<
  { [index: string]: IncomeTransactionsReportRecord[] },
  { [index: string]: RevenueByPaymentReportRecord },
  RevenueByPaymentReportRecord[]
>(
  R.values,
  R.mapObjIndexed((items, k) => ({
    method: k,
    value:
      items.reduce((acc, next) => acc.add(next.income), num(0)).value() ?? 0,
  }))
);
const groupByPaymentMethod = R.groupBy<IncomeTransactionsReportRecord>(
  R.prop('payment_type')
);

export const generateRevenueByPaymentReport = R.compose<
  IncomeTransactionsReportRecord[],
  { [index: string]: IncomeTransactionsReportRecord[] },
  RevenueByPaymentReportRecord[]
>(mapPayments, groupByPaymentMethod);

export const mapRevenueByPaymentReportToChartData = (
  payments: RevenueByPaymentReportRecord[]
) => {
  const [fixedItems, paymentTail] = R.splitAt(4, payments);
  const lastItem =
    paymentTail.length > 1
      ? [
          {
            method: '-1',
            value:
              paymentTail
                .reduce((acc, next) => acc.add(next.value), num(0))
                .value() ?? 0,
          },
        ]
      : paymentTail;
  const chartData = [...fixedItems, ...lastItem];
  const sortedChartData = sortDescendingByValue(chartData);
  const chartColors = [1, 0.8, 0.6, 0.4, 0.2].map((opacity) =>
    chroma(theme.primaryColor).alpha(opacity).css()
  );
  return sortedChartData.map(({ method, value }) => ({
    value,
    id: method,
    label: paymentMethods[method],
    color: chartColors.shift() as string,
  }));
};

// ==========================================================================
//                         Generate bestsellers report
// ==========================================================================

export const generateBestsellersReport = R.compose<
  ProductSoldReportRecord[],
  { [index: string]: ProductSoldReportRecord[] },
  { [index: string]: BestsellerOverview },
  BestsellerOverview[],
  BestsellerOverview[]
>(
  R.sortWith([R.descend(R.prop('sold'))]),
  R.values,
  R.mapObjIndexed((items, k) => ({
    id: k,
    name: items[0].product_name,
    sold: R.sum(R.pluck('sold_items', items)),
    returned: R.sum(R.pluck('returned_items', items)),
  })),
  R.groupBy(R.prop('product_id'))
);

// ==========================================================================
//                         Last transactions report
// ==========================================================================

export const mapLastTransactionsTableData = (data: {
  data: LastTransactionsTableInputData;
}): LastTransactionsTableEntry[] => {
  const { transactions } = data.data;

  return transactions.map((item) => ({
    id: item.id,
    date: item.date,
    invoice_number: item.invoice_number,
    income: item.income,
    payment_method: paymentMethods[item.payment_type],
    employee: item.employee,
    customer: item.customer,
  }));
};
// ==========================================================================
//                          Revenue and profit chart
// ==========================================================================

const formatDate = (date: string, type: ReportType) => {
  if (type === 'daily' || type === 'hourly') {
    const splitDate = date.split('.').slice(0, 2).join('/');
    if (type === 'daily') {
      return [date, splitDate];
    }
    if (type === 'hourly') {
      const hour = date.slice(date.length - 2, date.length);
      return [`${date}:00`, `${splitDate} ${hour}`];
    }
  }
  return [date, date];
};

export const mapRevenueAndProfitReportToChartData = R.map<
  BasicReportRecord,
  RevenueAndProfitChartData
>((item) => {
  const [originalDate, date] = formatDate(item.date, item.type);
  const profit = numOrZero(item.profit);
  const revenue = numOrZero(item.revenue);
  return {
    type: item.type,
    profit,
    revenue,
    originalDate,
    date,
  };
});

// ==========================================================================
//                          Sales report
// ==========================================================================

export const mapSalesReportsRecords = (reports: Array<SalesReport>) => {
  let n = 1;
  // eslint-disable-next-line no-plusplus
  return reports.map((r) => ({ ...r, position: n++ }));
};

// ====================================================================
//                    Batch reports download
// ====================================================================
const typeStringToEnumDict: Record<string, BatchReportType> = {
  invoice_returns: BatchReportType.InvoicesReturns,
  zreports: BatchReportType.ZReports,
  product_history: BatchReportType.ProductHistory,
  xls_report: BatchReportType.XLSReports,
  pre_accounting: BatchReportType.PreAccounting,
};

const statusStringToEnumDict: Record<string, BatchReportStatus> = {
  created: BatchReportStatus.Created,
  in_progress: BatchReportStatus.Processing,
  deleted: BatchReportStatus.Deleted,
  finished: BatchReportStatus.Finished,
};

export const stripBatchReportDetail = (r: BatchReportRawDetails) => ({
  ...R.pick(['id', 'number_of_files'], r),
  zip_file: r.zip_file ? r.zip_file : null,
  status: statusStringToEnumDict[r.status],
  type: typeStringToEnumDict[r.batch_type],
});

export const normalizeBatchReportRecord = (r: BatchReportRecord) => ({
  year: parseInt(r.year, 10),
  month: dayjs(r.reports[0].start_date).month(),
  reports: r.reports.map(stripBatchReportDetail),
});

export const mapBatchReportsToMonthlyReports = R.map(
  normalizeBatchReportRecord
);

export function mapJobStatusToBatchReportStatus(s: JobState) {
  switch (s) {
    case JobState.Created:
    case JobState.InProgress:
    case JobState.Queued:
      return BatchReportStatus.Processing;
    case JobState.Completed:
      return BatchReportStatus.Finished;
    case JobState.Failed:
      return BatchReportStatus.Failed;
    default:
      return assertNever(s);
  }
}

export function formatTipDate(inputDate: string) {
  const formattedDate = dayjs(inputDate).format('DD.MM.YYYY').split(' ');
  const [date] = formattedDate;
  return date;
}

export const euroFormatter = (value: string | number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
    typeof value === 'string' ? parseFloat(value) : value
  );
